import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import config from '../config';

const CallDurationPage = () => {
  const { customAgentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalCalls: 0,
    totalDuration: 0,
    agentDuration: 0,
    transferDuration: 0
  });
  const [agentName, setAgentName] = useState('');
  const [user, setUser] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // 30 days back
    endDate: new Date() // current date
  });

  // Add debugging for date changes
  useEffect(() => {
    console.log('Date range changed:', {
      startDate: dateRange.startDate?.toISOString(),
      endDate: dateRange.endDate?.toISOString(),
      customAgentId
    });
  }, [dateRange.startDate, dateRange.endDate, customAgentId]);

  useEffect(() => {
    const fetchAgentInfo = async () => {
      try {
        // Get agent information to display the name
        const agentResponse = await axios.get(`${config.apiBaseUrl}/api/agents/${customAgentId}`, {
          withCredentials: true
        });
        if (agentResponse.data && agentResponse.data.name) {
          setAgentName(agentResponse.data.name);
        } else {
          // Fallback to customAgentId if name is not available
          setAgentName(customAgentId);
        }
      } catch (err) {
        console.error('Error fetching agent info:', err);
        // If we can't get the agent name, we'll just use the ID
        setAgentName(customAgentId);
      }
    };

    if (customAgentId) {
      fetchAgentInfo();
    }
  }, [customAgentId]);

  useEffect(() => {
    // Get user from localStorage and set it
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  useEffect(() => {
    const fetchCallStats = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get the user from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
          throw new Error('User not authenticated');
        }

        // Always use the customAgentId from URL parameters for this page
        const agentId = customAgentId;
        if (!agentId) {
          throw new Error('Agent ID not found');
        }

        // Format dates for API
        const startDate = dateRange.startDate ? dateRange.startDate.toISOString() : null;
        const endDate = dateRange.endDate ? new Date(dateRange.endDate.setHours(23, 59, 59, 999)).toISOString() : null;

        console.log('Making API call with params:', {
          agentId,
          startDate,
          endDate
        });

        // Get the call statistics
        const response = await axios.get(`${config.apiBaseUrl}/api/call-reports/agent/${agentId}/stats`, {
          params: {
            startDate,
            endDate
          }
        });
        
        console.log('API response:', response.data);
        
        if (response.data) {
          setStats({
            totalCalls: response.data.totalCalls || 0,
            totalDuration: response.data.totalDuration || 0,
            agentDuration: response.data.agentDuration || 0,
            transferDuration: response.data.transferDuration || 0
          });
        }
      } catch (err) {
        console.error('Error fetching call stats:', err);
        setError(err.response?.data?.message || 'Failed to fetch call statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchCallStats();
  }, [customAgentId, dateRange.startDate, dateRange.endDate]);

  // Calculate total call duration: callDuration + (totalCallDuration - callDuration)
  // Only if totalCallDuration is numeric and > callDuration
  const calculateTotalCallDuration = () => {
    const agentDuration = stats.agentDuration || 0;
    const transferDuration = stats.transferDuration || 0;
    
    // Total = agentDuration + transferDuration
    // This is equivalent to: callDuration + (totalCallDuration - callDuration) = totalCallDuration
    // But only if totalCallDuration was valid (transferDuration > 0 means totalCallDuration > callDuration)
    // If transferDuration is 0, it means either totalCallDuration doesn't exist or totalCallDuration <= callDuration
    // In that case, we just use agentDuration (callDuration)
    if (transferDuration > 0) {
      return agentDuration + transferDuration;
    } else {
      return agentDuration; // Fallback to agentDuration if transferDuration is 0
    }
  };

  // Format total seconds to hours, minutes, seconds
  const formatTotalDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <Container sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading call duration statistics...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4,
        gap: 2
      }}>
        <IconButton onClick={() => navigate(`/agents/${customAgentId}/call-reports`)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Call Duration Statistics
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 2 }}>
          Agent: {agentName || 'Loading...'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Date Range Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Date Range
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => {
                console.log('Start date changed to:', newValue);
                setDateRange(prev => ({ ...prev, startDate: newValue }));
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 200 }
                }
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => {
                console.log('End date changed to:', newValue);
                setDateRange(prev => ({ ...prev, endDate: newValue }));
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 200 }
                }
              }}
            />
          </Box>
        </LocalizationProvider>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Total Number of Calls
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.totalCalls}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Calls in the selected date range
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Total Call Duration
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                {formatTotalDuration(calculateTotalCallDuration())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total duration: callDuration + (totalCallDuration - callDuration) in the selected date range
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {user && user.role === 'admin' && (
          <>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Agent Duration
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {formatTotalDuration(stats.agentDuration)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total agent duration (from callDuration) in the selected date range
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Transfer Duration
                  </Typography>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {formatTotalDuration(stats.transferDuration)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total transfer duration (totalCallDuration - callDuration) in the selected date range
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Additional Info */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <Typography variant="body1" paragraph>
          This page shows call duration statistics for the selected agent. You can adjust the date range 
          to view statistics for different time periods. The data includes:
        </Typography>
        <Typography variant="body2" component="div" sx={{ pl: 2 }}>
          <ul>
            <li><strong>Total Call Duration:</strong> Sum of callDuration for all calls in the date range. </li>
            {user && user.role === 'admin' && (
              <>
                <li><strong>Agent Duration:</strong> Sum of callDuration (agent's direct call time) for all calls in the date range</li>
                <li><strong>Transfer Duration:</strong> Sum of (totalCallDuration - callDuration) for all calls in the date range. Only calculated when totalCallDuration is numeric and greater than callDuration.</li>
              </>
            )}
          </ul>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Date Range: {dateRange.startDate?.toLocaleDateString()} - {dateRange.endDate?.toLocaleDateString()}
        </Typography>
      </Paper>
    </Container>
  );
};

export default CallDurationPage; 